import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, Brain, ArrowRight, Check, Lock, Zap, TrendingUp, Users, Building2, Sparkles, ShieldCheck, Globe, Cpu } from 'lucide-react';
import './WhoIsItFor.css';

const WhoIsItFor = () => {
  const navigate = useNavigate();

  return (
    <section className="audience-section">
      <div className="audience-container">
        <motion.div 
          className="audience-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="section-title">Our products</h2>
          <p className="section-subtitle">Three powerful solutions. One mission: Unlock human potential through technology.</p>
        </motion.div>

        <div className="audience-rows">
          {/* Row 1: WhiteMatter - Cognitive Performance (Image Left, Text Right) */}
          <div className="audience-row">
            <motion.div 
              className="audience-visual visual-whitematter"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="visual-content">
                <Brain size={72} className="visual-icon" />
                <div className="visual-badge">
                  <Sparkles size={16} />
                  <span>WhiteMatter</span>
                </div>
                <div className="visual-stats">
                  <div className="stat-item">
                    <TrendingUp size={20} />
                    <span>+50% Productivity</span>
                  </div>
                  <div className="stat-item">
                    <Zap size={20} />
                    <span>Real-Time Feedback</span>
                  </div>
                </div>
              </div>
              <div className="visual-glow-purple"></div>
            </motion.div>

            <motion.div 
              className="audience-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="eyebrow">WhiteMatter</span>
              <h3>Peak Cognitive Performance.</h3>
              <p>
                AI-powered neurofeedback that tracks your brain activity in real-time. 
                Optimize focus, reduce stress, and boost productivity with personalized cognitive insights. 
                Perfect for professionals, athletes, and anyone seeking mental clarity.
              </p>
              
              <ul className="feature-list">
                <li>
                  <Check className="check-icon" size={20} />
                  <span>24/7 real-time brain monitoring & feedback</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>AI-driven personalized cognitive training</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>3000+ data points processed per hour</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Non-invasive digital biometric tracking</span>
                </li>
              </ul>

              <button className="text-link" onClick={() => navigate('/products')}>
                Explore WhiteMatter <ArrowRight size={18} />
              </button>
            </motion.div>
          </div>

          {/* Row 2: SafePulse - Cybersecurity (Text Left, Image Right) */}
          <div className="audience-row reverse">
            <motion.div 
              className="audience-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="eyebrow">SafePulse</span>
              <h3>Invisible Security. Unbreakable Protection.</h3>
              <p>
                ML-powered behavioral biometric authentication accelerated by Intel® Core™ Ultra processors. 
                Creates your unique digital DNA with zero user friction, real-time threat detection, and local data privacy. 
                Protect enterprises, financial institutions, and government systems with authentication that can't be stolen.
              </p>
              
              <ul className="feature-list">
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Intel® optimized ML for lightning-fast processing</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>85-92% reduction in credential-based fraud</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Zero user friction - works invisibly</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>60% lower IT costs & 25-40% insurance savings</span>
                </li>
              </ul>

              <button className="text-link" onClick={() => navigate('/safepulse')}>
                Explore SafePulse <ArrowRight size={18} />
              </button>
            </motion.div>

            <motion.div 
              className="audience-visual visual-safepulse"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="visual-content">
                <Shield size={72} className="visual-icon" />
                <div className="visual-badge">
                  <Lock size={16} />
                  <span>SafePulse</span>
                </div>
                <div className="visual-stats">
                  <div className="stat-item">
                    <Cpu size={20} />
                    <span>Intel® Optimized</span>
                  </div>
                  <div className="stat-item">
                    <Zap size={20} />
                    <span>ML-Powered</span>
                  </div>
                </div>
              </div>
              <div className="visual-glow-blue"></div>
            </motion.div>
          </div>

          {/* Row 3: TrustMark - Bot Protection (Image Left, Text Right) */}
          <div className="audience-row">
            <motion.div 
              className="audience-visual visual-trustmark"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="visual-content">
                <ShieldCheck size={72} className="visual-icon" />
                <div className="visual-badge">
                  <Globe size={16} />
                  <span>TrustMark</span>
                </div>
                <div className="visual-stats">
                  <div className="stat-item">
                    <Shield size={20} />
                    <span>Bot Protection</span>
                  </div>
                  <div className="stat-item">
                    <Zap size={20} />
                    <span>Real-Time Defense</span>
                  </div>
                </div>
              </div>
              <div className="visual-glow-green"></div>
            </motion.div>

            <motion.div 
              className="audience-content"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="eyebrow">TrustMark</span>
              <h3>Advanced Bot Protection. Seamless Experience.</h3>
              <p>
                Revolutionize online access control by verifying genuine users and protecting your site from malicious automated threats. 
                Advanced security designed around your needs with enhanced experience and privacy-focused protection.
              </p>
              
              <ul className="feature-list">
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Seamless authentication without compromising security</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Privacy-focused - no personal data collection</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Real-time threat detection & prevention</span>
                </li>
                <li>
                  <Check className="check-icon" size={20} />
                  <span>Integrated with existing systems seamlessly</span>
                </li>
              </ul>

              <button className="text-link" onClick={() => navigate('/trustmark')}>
                Explore TrustMark <ArrowRight size={18} />
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoIsItFor;
